/**
 * One person in the follow-ups queue: their name (opening the conversation),
 * the leading reason with how long it has waited, a chip for each other
 * reason, each listing that fits their budget with "Not a fit", and Message,
 * Call, Snooze and Mark cold/lost.
 *
 * Message and Call are links, to wa.me with the ready message and to tel:.
 * Tapping one records nothing; it stores a pending hand-off for the outcome
 * prompt (`onHandoff`). `checkingWhatsapp` shows a quiet "Checking
 * WhatsApp…" note while the queue looks for the message WhatsApp recorded.
 */

import { Archive, Loader2, MessageCircle, Phone } from "lucide-react";
import { forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useDismissListingMatch } from "@/features/realtor/hooks/use-realtor-crm";
import {
	type FollowUpEntry,
	type FollowUpReason,
	telUrl,
	whatsappUrl,
} from "@/features/realtor/services/realtor-crm-api.service";
import { errorMessage } from "@/features/realtor/utils/error-message";
import { formatPaiseAsRupees } from "@/features/realtor/utils/listings-format";
import { ContactInitials } from "../contact-initials";
import {
	ACTION_PRIMARY,
	ACTION_SECONDARY,
	reasonLabel,
	waitingLabel,
} from "./follow-up-format";
import { MarkLeadsSheet } from "./mark-leads-sheet";
import { SnoozeMenu } from "./snooze-menu";
import type { HandoffAction } from "./use-pending-handoff";

export const FollowUpCard = forwardRef<
	HTMLElement,
	{
		entry: FollowUpEntry;
		onHandoff: (entry: FollowUpEntry, action: HandoffAction) => void;
		checkingWhatsapp?: boolean;
	}
>(function FollowUpCard({ entry, onHandoff, checkingWhatsapp = false }, ref) {
	const { t } = useTranslation();
	const [marking, setMarking] = useState(false);
	const [snoozeError, setSnoozeError] = useState<string | null>(null);
	const { contact } = entry;
	const listingMatches = entry.reasons.filter(
		(reason) =>
			reason.type === "listing_match" &&
			reason.leadId &&
			reason.details.listingId,
	);
	const otherTypes = [
		...new Set(
			entry.reasons
				.map((reason) => reason.type)
				.filter((type) => type !== entry.leadingReason),
		),
	];
	const waUrl = whatsappUrl(
		contact.phoneCountryCode,
		contact.phone,
		entry.readyMessage,
	);
	const callUrl = telUrl(contact.phoneCountryCode, contact.phone);
	const headline = t("realtor.followUps.card.headline", {
		reason: reasonLabel(entry.leadingReason, t),
		waiting: waitingLabel(entry.leadingReason, entry.waitingMs, t),
	});

	return (
		<article
			ref={ref}
			tabIndex={-1}
			aria-label={t("realtor.followUps.card.aria", { name: contact.name })}
			data-testid="follow-up-card"
			className="space-y-2 px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-dashboard-accent/40"
		>
			<div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between xl:gap-4">
				<div className="flex min-w-0 flex-1 items-start gap-3">
					<Avatar
						className="mt-0.5 h-8 w-8 shrink-0 border border-dashboard-border"
						aria-hidden
					>
						<AvatarFallback className="bg-dashboard-border text-[11px] font-semibold text-dashboard-text">
							<ContactInitials name={contact.name} />
						</AvatarFallback>
					</Avatar>
					<div className="min-w-0 flex-1 space-y-1">
						<div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
							<Link
								to={`/realtor/messages?contact=${contact.id}`}
								className="min-w-0 truncate text-base font-medium text-dashboard-text hover:underline"
							>
								{contact.name}
							</Link>
							<p className="text-sm text-dashboard-muted">{headline}</p>
						</div>
						{checkingWhatsapp && (
							<p
								role="status"
								className="flex items-center gap-1.5 text-xs text-dashboard-muted"
							>
								<Loader2
									className="h-3 w-3 motion-safe:animate-spin"
									aria-hidden
								/>
								{t("realtor.followUps.card.checkingWhatsapp")}
							</p>
						)}
						{otherTypes.length > 0 && (
							<ul
								className="flex flex-wrap gap-1.5"
								aria-label={t("realtor.followUps.card.otherReasons")}
							>
								{otherTypes.map((type) => (
									<li
										key={type}
										className="rounded-full border border-dashboard-border bg-dashboard-surface px-2.5 py-0.5 text-xs text-dashboard-muted"
									>
										{reasonLabel(type, t)}
									</li>
								))}
							</ul>
						)}
						{listingMatches.length > 0 && (
							<ul
								className="space-y-1"
								aria-label={t("realtor.followUps.card.listingMatches")}
							>
								{listingMatches.map((reason) => (
									<ListingMatchRow
										key={`${reason.leadId}:${reason.details.listingId}`}
										reason={reason}
										contactName={contact.name}
									/>
								))}
							</ul>
						)}
					</div>
				</div>

				<div className="grid shrink-0 grid-cols-[minmax(0,1fr)_2.75rem_2.75rem_2.75rem] gap-2 xl:flex xl:justify-end">
					{waUrl ? (
						<a
							href={waUrl}
							target="_blank"
							rel="noopener noreferrer"
							className={`ph-no-capture ${ACTION_PRIMARY}`}
							aria-label={t("realtor.followUps.card.messageAria", {
								name: contact.name,
							})}
							onClick={() => onHandoff(entry, "message")}
						>
							<MessageCircle className="h-4 w-4" aria-hidden />
							<span className="truncate">
								{t("realtor.followUps.card.message")}
							</span>
						</a>
					) : (
						<button
							type="button"
							disabled
							className={ACTION_PRIMARY}
							title={t("realtor.followUps.card.noPhone")}
						>
							<MessageCircle className="h-4 w-4" aria-hidden />
							<span className="truncate">
								{t("realtor.followUps.card.message")}
							</span>
						</button>
					)}
					{callUrl ? (
						<a
							href={callUrl}
							className={`ph-no-capture ${ACTION_SECONDARY}`}
							aria-label={t("realtor.followUps.card.callAria", {
								name: contact.name,
							})}
							onClick={() => onHandoff(entry, "call")}
						>
							<Phone className="h-4 w-4" aria-hidden />
							<span className="max-xl:sr-only">
								{t("realtor.followUps.card.call")}
							</span>
						</a>
					) : (
						<button
							type="button"
							disabled
							className={ACTION_SECONDARY}
							title={t("realtor.followUps.card.noPhone")}
						>
							<Phone className="h-4 w-4" aria-hidden />
							<span className="max-xl:sr-only">
								{t("realtor.followUps.card.call")}
							</span>
						</button>
					)}
					<SnoozeMenu
						contactId={contact.id}
						contactName={contact.name}
						onError={setSnoozeError}
					/>
					<button
						type="button"
						className={ACTION_SECONDARY}
						aria-label={t("realtor.followUps.card.markAria", {
							name: contact.name,
						})}
						onClick={() => setMarking(true)}
					>
						<Archive className="h-4 w-4 xl:hidden" aria-hidden />
						<span className="max-xl:sr-only">
							{t("realtor.followUps.card.mark")}
						</span>
					</button>
				</div>
			</div>
			{snoozeError && (
				<p role="alert" className="text-sm text-dashboard-pill-risk">
					{snoozeError}
				</p>
			)}
			{marking && (
				<MarkLeadsSheet entry={entry} open onOpenChange={setMarking} />
			)}
		</article>
	);
});

/** A listing that fits the person's budget, with "Not a fit" (R28). */
function ListingMatchRow({
	reason,
	contactName,
}: {
	reason: FollowUpReason;
	contactName: string;
}) {
	const { t } = useTranslation();
	const dismiss = useDismissListingMatch();
	const [error, setError] = useState<string | null>(null);
	const { listingId, listingHeadline, askingPrice, count } = reason.details;
	const headline = listingHeadline ?? "";
	const others = Number(count) - 1;

	const notAFit = async () => {
		if (!reason.leadId || !listingId) return;
		setError(null);
		try {
			await dismiss.mutateAsync({ leadId: reason.leadId, listingId });
		} catch (failure: unknown) {
			setError(
				errorMessage(failure, t("realtor.followUps.card.notAFitFailed")),
			);
		}
	};

	return (
		<li>
			<div className="flex items-center gap-2">
				<div className="min-w-0">
					<p className="truncate text-sm text-dashboard-text">
						{t("realtor.followUps.card.listingMatch", {
							headline,
							price: formatPaiseAsRupees(Number(askingPrice)),
						})}
					</p>
					{others > 0 && (
						<p className="text-xs text-dashboard-muted">
							{t("realtor.followUps.card.moreMatches", { count: others })}
						</p>
					)}
				</div>
				<button
					type="button"
					className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-md px-1 text-xs font-medium text-dashboard-muted underline-offset-2 hover:text-dashboard-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dashboard-accent/40 disabled:opacity-60 xl:min-h-0 xl:py-0.5"
					disabled={dismiss.isPending}
					aria-label={t("realtor.followUps.card.notAFitAria", {
						headline,
						name: contactName,
					})}
					onClick={() => void notAFit()}
				>
					{dismiss.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
					{t("realtor.followUps.card.notAFit")}
				</button>
			</div>
			{error && (
				<p role="alert" className="mt-1 text-xs text-dashboard-pill-risk">
					{error}
				</p>
			)}
		</li>
	);
}
